import React, { useState } from 'react';
import { PlusIcon, MinusIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqs = [{
    question: '¿Este ebook es para mí si ya intenté hacer dieta y abandoné?',
    answer: 'Sí. Justamente está pensado para personas que ya probaron dietas, arrancaron motivadas y después dejaron todo porque pasaban hambre, no veían resultados o no tenían un plan claro. Plan Anti Panza no busca que hagas una dieta perfecta, sino que sigas un sistema simple de 12 semanas con alimentación, movimiento, hábitos y control real del progreso.'
  }, {
    question: '¿Tengo que pasar hambre para bajar 3 kg por mes?',
    answer: 'No. La idea del ebook es enseñarte a crear un déficit calórico inteligente, pero sin vivir con hambre todo el día. Vas a aprender a comer con más proteína, más volumen, mejores porciones y alimentos más saciantes para bajar grasa sin sentir que estás castigándote.'
  }, {
    question: '¿Voy a tener que dejar el pan, los carbohidratos o las comidas que me gustan?',
    answer: 'No. El objetivo no es eliminar todo lo que te gusta, sino aprender a controlarlo. Vas a entender cómo incluir carbohidratos, comidas ricas y algún gusto sin que eso arruine tu progreso. La clave no es prohibirte todo, es aprender a comer mejor.'
  }, {
    question: '¿Necesito ir al gimnasio todos los días?',
    answer: 'No. No necesitás vivir en el gimnasio para ver cambios. El ebook te enseña cómo combinar entrenamiento de fuerza, pasos diarios y cardio inteligente para perder grasa, mantener músculo y verte más firme sin entrenar 2 horas por día.'
  }, {
    question: '¿Qué pasa si no sé cuántas calorías tengo que comer?',
    answer: 'No pasa nada, el ebook te guía paso a paso. Vas a aprender a calcular tus calorías de mantenimiento, armar tu déficit y ajustar según tu progreso. La idea es que dejes de adivinar y sepas exactamente por dónde empezar.'
  }, {
    question: '¿Este plan sirve si como "más o menos sano" pero igual no bajo?',
    answer: 'Sí. Muchas personas comen "sano", pero no están en déficit calórico sin darse cuenta. En el ebook vas a aprender a detectar calorías invisibles, alimentos saludables pero muy calóricos, errores comunes y cómo ordenar tus comidas para que comer sano también te ayude a bajar grasa.'
  }, {
    question: '¿Y si tengo poco tiempo para cocinar o vivo improvisando?',
    answer: 'También está pensado para eso. Además del ebook, tenés bonos como el Menú Anti Panza de 4 Semanas, 15 comidas altas en proteína, 15 desayunos altos en proteína, 15 postres bajos en calorías y el Sistema de Meal Prep, para que tengas opciones simples y no dependas de improvisar cuando tenés hambre.'
  }, {
    question: '¿Qué pasa si fallo un día o me salgo del plan?',
    answer: 'No tenés que empezar de cero. Una de las claves del sistema es aprender a volver rápido al plan sin culpa. Una comida fuera del plan no arruina tu progreso. Lo que realmente te frena es abandonar por completo.'
  }, {
    question: '¿Voy a perder músculo o quedar flaco y blando?',
    answer: 'El objetivo del ebook no es que solo bajes peso en la balanza, sino que pierdas grasa manteniendo la mayor cantidad posible de músculo. Por eso el sistema incluye proteína suficiente, entrenamiento de fuerza, déficit moderado y seguimiento del progreso real para que puedas verte más firme y definido.'
  }, {
    question: '¿En cuánto tiempo puedo empezar a ver resultados?',
    answer: 'Si aplicás el sistema con constancia, podés empezar a notar cambios en las primeras semanas: menos hinchazón, más control con la comida, mejor energía, ropa más cómoda y reducción progresiva de peso y medidas. La promesa del plan es ayudarte a bajar al menos 3 kg por mes durante 12 semanas, siempre que sigas la estructura y ajustes según tu punto de partida.'
  }];
  return <section className="py-14 md:py-20 bg-black pt-[2px] pb-[2px]" data-id="element-77">
      <div className="container mx-auto px-5 sm:px-6 md:px-8" data-id="element-78">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="max-w-4xl mx-auto" data-id="element-79">
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-display text-white md:mb-24 text-center uppercase tracking-tight leading-[0.95] mt-[10px] mb-[10px]" data-id="element-80">
            Preguntas Frecuentes
          </h2>

          <div className="space-y-4" data-id="element-81">
            {faqs.map((faq, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: 0.03 * index
          }} className="bg-zinc-900/50 border border-zinc-800 rounded-2xl overflow-hidden" data-id="element-82">
                <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full flex items-center justify-between p-6 md:p-8 text-left hover:bg-zinc-800/50 transition-colors" data-id="element-83">
                  <span className="text-lg md:text-xl font-bold text-white pr-6" data-id="element-84">
                    {faq.question}
                  </span>
                  <motion.div animate={{
                rotate: openIndex === index ? 180 : 0
              }} transition={{
                duration: 0.3
              }} data-id="element-85">
                    {openIndex === index ? <MinusIcon className="w-6 h-6 text-orange-500 flex-shrink-0" data-id="element-86" /> : <PlusIcon className="w-6 h-6 text-orange-500 flex-shrink-0" data-id="element-87" />}
                  </motion.div>
                </button>

                <AnimatePresence data-id="element-88">
                  {openIndex === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-89">
                      <div className="px-6 md:px-8 pb-6 md:pb-8 text-zinc-300 text-lg leading-relaxed border-t border-zinc-800 pt-6 mt-2 mx-6 md:mx-8" data-id="element-90">
                        {faq.answer}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}